<script setup lang="ts">
import type { AwardConfig } from '~/types/appTypes'
import _shuffle from 'lodash/shuffle'

const app = useNuxtApp()
const route = useRoute()
const kijidasConfig = useAppConfig().kijidas
const storeIndex = useStoreIndex()
const storeArticleList = useStoreArticleList()
const storeMuseumChar = useStoreMuseumChar()

definePageMeta({ layout: 'layout-side-default' })

const awardConfigList = useAppConfig().app.award
const awardConfig = awardConfigList[route.params.award as string] as AwardConfig

const baseTitle = awardConfig.baseTitle
const breadcrumbTitle = awardConfig.breadcrumbTitle || baseTitle
let headTitle = ''
let headDescription = ''

const year = app.$anyToInteger(route.params.year)

const pageData = {
  resultHistory: [],
  topStaticItem: null as Article,
}

const refData = reactive({
  randomItems: [],
  entryMode: null,
  isResult: false,
  isPreview: false,
  isMounted: false,
})

const setHead = () => {
  headTitle = `${baseTitle}${year}`
  headDescription = awardConfig.description

  // ぱんくず作成
  storeIndex.setBreadCrumbs([
    {
      text: breadcrumbTitle,
      disabled: false,
      to: {
        name: 'award',
        params: { award: awardConfig.awardSlug },
      },
    },
    {
      text: headTitle,
      disabled: true,
    },
  ])

  const headInput = app.$makeMeta({
    title: headTitle,
    description: headDescription,
  })
  useHead(headInput)
}

const initPage = async () => {
  //  static全カテゴリ取得
  const [_resSite, _resMaster, resStaticCat] = await Promise.all([
    storeIndex.fetchSite(),
    storeIndex.fetchMaster(),
    storeArticleList.fetchCategories('static'),
  ])

  let entryMode = null

  if (route.name === 'preview-award') {
    // tod: プレビューモード
    entryMode = 'in'
    refData.isPreview = true
  } else {
    //  このアワードを表示していいか
    entryMode = app.$isAwardEntry(awardConfig, year)
    if (entryMode === null) {
      //  表示できない
      throw createError({ statusCode: 404 })
      return
    }
  }
  refData.entryMode = entryMode

  const staticCats = resStaticCat.data?.Categories as ArticleCategory[]
  if (!staticCats?.length) {
    throw createError({
      statusCode: 999,
      statusMessage: 'カテゴリ取得エラー(static)',
    })
    return
  }
  //  カテゴリから url param xxxx年のID取得
  const catSlug = awardConfig.categorySlugPrefix + year
  const catId = app.$getCategoryBySlug(staticCats, catSlug)
  if (!catId) {
    throw createError({
      statusCode: 999,
      statusMessage: `ID取得エラー(${awardConfig.awardSlug})`,
    })
    return
  }

  //  awardカテゴリ取得
  const resMuseumCat = await storeArticleList.fetchCategories(awardConfig.awardSlug)
  const charaCats = resMuseumCat.data.Categories as ArticleCategory[]
  if (!charaCats) {
    throw createError({
      statusCode: 999,
      statusMessage: `記事カテゴリ取得エラー(${awardConfig.awardSlug})`,
    })
    return
  }
  //  awardカテゴリから xxxx年のカテゴリid取得
  const charaCatId = app.$getCategoryBySlug(charaCats, catSlug)

  //  staticカテゴリID と awardカテゴリID からbodyとキャラ取得
  const [resStatic, resMuseumCharList] = await Promise.all([
    storeArticleList.fetchList('awardStatic', { query: { category: catId } }),
    storeMuseumChar.fetchCharaList(kijidasConfig, awardConfig.awardSlug, year, charaCatId),
  ])
  const articles = resStatic.data.articles as Article[]
  pageData.topStaticItem = articles[0]
  refData.randomItems = resMuseumCharList

  //  このアワードの結果ページがあるか
  refData.isResult = !!app.$isAwardResult(awardConfig, year)

  //  過去の結果ページslugの取得
  const resultMaster = storeIndex.state.master[awardConfig.master.result]
  const resultHistory = []
  if (resultMaster?.details?.length) {
    //  リンクを作成
    resultMaster.details.forEach((result) => {
      const item = {
        year: result.text,
        to: {
          name: 'award-year-result',
          params: {
            award: route.params.award,
            year: result.text,
          },
        },
      }
      resultHistory.push(item)
    })
    pageData.resultHistory = resultHistory
  }
}
await initPage()
setHead()
onMounted(() => {
  refData.randomItems = _shuffle([...refData.randomItems])
  refData.isMounted = true
})
</script>

<template>
  <div class="p-articlePage">
    <div
      v-if="pageData.topStaticItem"
      id="staticBody"
      v-html="app.$getArticleBody(pageData.topStaticItem)"
    />

    <div class="p-museumChar">
      <nuxt-link
        v-if="refData.isResult"
        class="p-museumChar_result"
        v-bind:to="{ name: 'award-year-result', params: { award: route.params.award, year: year } }"
      >
        投票結果発表
      </nuxt-link>
      <nuxt-link
        v-else-if="refData.entryMode === 'in'"
        class="p-museumChar_result"
        v-bind:to="{ name: 'award-year-result', params: { award: route.params.award, year: year } }"
      >
        途中経過を見る
      </nuxt-link>

      <div
        v-if="refData.entryMode === 'after'"
        class="p-museumChar_pastEntry"
      >
        投票は締め切りました。たくさんの応援ありがとうございました。
      </div>

      <div class="p-museumChar_resultHistory">
        <div class="p-museumChar_head">
          <img
            class="p-museumChar_head_icon"
            src="@/assets/images/icon-title.png"
          >
          <span
            class="p-museumChar_head_title"
          >
            過去の結果
          </span>
        </div>
        <div class="p-museumChar_resultHistory_years">
          <template
            v-for="result in pageData.resultHistory"
            v-bind:key="result.year"
          >
            <nuxt-link
              class="p-museumChar_resultHistory_year"
              v-bind:to="result.to"
            >
              {{ result.year }}
            </nuxt-link>
          </template>
        </div>
      </div>

      <template
        v-if="refData.randomItems.length && refData.isMounted"
      >
        <div class="p-museumChar_head">
          <img
            class="p-museumChar_head_icon"
            src="@/assets/images/icon-title.png"
          >
          <span
            class="p-museumChar_head_title"
          >
            エントリー （全 {{ refData.randomItems.length }} 点）
          </span>
        </div>
        <p
          v-if="refData.entryMode === 'in'"
          class="p-museumChar_info"
        >
          画像をクリックするとプロフィールが表示され、投票ができます。
        </p>
        <p
          v-else
          class="p-museumChar_info"
        >
          画像をクリックするとプロフィールが表示されます。
        </p>

        <div class="p-museumChar_charaList">
          <AwardItem
            v-for="item in refData.randomItems"
            v-bind:key="item.id"
            v-bind:item="item"
            v-bind:preview="refData.isPreview"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<style lang = "scss" scoped>
@use "@/assets/css/foundation/mixin/responsive" as responsive;

#staticBody {
  :deep(h1) {
    margin-bottom: 20px;
    color: #252525;
    font-family: "Noto Sans JP", sans-serif;
    font-size: 26px;
    font-weight: 400;
    line-height: 1.3;
  }

  :deep(img[width="644"]) {
    width: 100%;
  }
}

.p-museumChar {
  &_head {
    display: flex;
    align-items: flex-start;
    margin-bottom: 10px;
    padding-bottom: 12px;
    border-bottom: solid 1px var(--g_color_border_gray);

    &_icon {
      margin-top: 3px;
      margin-right: 15px;

      @include responsive.mq_down(sp) {
        width: 20px;
        height: 20px;
      }
    }

    &_title {
      display: inline-block;
      position: relative;
      width: 100%;
      margin-right: 10px;
      font-family: var(--g_font_family_SawarabiGothic);
      font-size: 20px;
      font-weight: bold;
      line-height: 1.5;

      @include responsive.mq_down(sp) {
        font-size: 18px;
      }
    }
  }

  &_info {
    color: var(--color_text_red);
  }

  &_pastEntry {
    margin: 20px 0;
    padding: 20px;
    background-color: #ddd;
    color: var(--color_error);
    font-size: 20px;
    font-weight: bold;
    text-align: center;

    @include responsive.mq_down(sp) {
      padding: 15px;
      font-size: 16px;
    }
  }

  &_result {
    display: block;
    width: 100%;
    margin: 20px 0;
    margin-right: 5px;
    padding: 15px;
    border-radius: 5px;
    background-color: v-bind("awardConfig.primaryColor");
    color: #fff;
    font-size: 20px;
    font-weight: bold;
    line-height: 38px;
    text-align: center;

    @include responsive.mq_down(sp) {
      font-size: 16px;
    }

    &:hover {
      opacity: 0.7;
      text-decoration: none;
    }
  }

  &_resultHistory {
    margin-top: 40px;
    margin-bottom: 50px;

    &_years {
      display: flex;
      flex-wrap: wrap;
    }

    &_year {
      width: 62px;
      height: 38px;
      margin-top: 10px;
      margin-right: 5px;
      border-radius: 5px;
      background-color: v-bind("awardConfig.primaryColor");
      color: #fff;
      font-size: 18px;
      font-weight: bold;
      line-height: 38px;
      text-align: center;

      &:hover {
        opacity: 0.7;
        text-decoration: none;
      }
    }
  }

  &_charaList {
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    margin: 0 -8px;

    @include responsive.mq_down(sp) {
      display: block;
    }
  }
}
</style>
